import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Mail, Search, Download, Trash2, UserCheck, UserX, 
  Users, TrendingUp, Filter, RefreshCw, Plus, X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MailingListPage = () => {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const fetchMailingList = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (subscriptionFilter) params.append('subscription', subscriptionFilter);
      if (sourceFilter) params.append('source', sourceFilter);
      
      const response = await fetch(`${API_URL}/api/admin/mailing-list?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setEntries(data);
    } catch (error) {
      console.error('Failed to fetch mailing list:', error);
    }
  }, [token, searchTerm, statusFilter, subscriptionFilter, sourceFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/mailing-list/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchMailingList(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchMailingList, fetchStats]);

  const handleExport = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/mailing-list/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mailing_list_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };

  const handleUpdateStatus = async (entryId, field, value) => {
    try {
      const body = {};
      if (field === 'subscription') {
        body.subscription_status = value;
      } else if (field === 'account') {
        body.account_status = value;
      }
      
      await fetch(`${API_URL}/api/admin/mailing-list/${entryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      
      fetchMailingList();
      fetchStats();
    } catch (error) {
      console.error('Failed to update:', error);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Remove this email from the mailing list?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/mailing-list/${entryId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchMailingList();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    
    try {
      await fetch(`${API_URL}/api/admin/mailing-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email: newEmail,
          first_name: newFirstName,
          last_name: newLastName,
          source: 'Manual'
        })
      });
      
      setShowAddModal(false);
      setNewEmail('');
      setNewFirstName('');
      setNewLastName('');
      fetchMailingList();
      fetchStats();
    } catch (error) {
      console.error('Failed to add email:', error);
    } finally {
      setAddLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setSubscriptionFilter('');
    setSourceFilter('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: '2-digit'
    });
  };

  const EntryCard = ({ entry }) => (
    <div className="p-3 sm:p-4 bg-cloud-white rounded-lg">
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-carbon-black text-sm truncate">{entry.email}</p>
          <p className="text-xs text-slate-gray truncate">
            {entry.first_name || entry.last_name 
              ? `${entry.first_name || ''} ${entry.last_name || ''}`.trim()
              : 'No name'}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleDelete(entry.id)}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 flex-shrink-0"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1 mb-2">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
          entry.source === 'User Registration' 
            ? 'bg-blue-100 text-blue-700'
            : entry.source === 'Contact Form'
            ? 'bg-purple-100 text-purple-700'
            : 'bg-gray-100 text-gray-700'
        }`}>
          {entry.source}
        </span>
        <span className="text-xs text-slate-gray">{formatDate(entry.signup_date)}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Select 
          value={entry.account_status} 
          onValueChange={(value) => handleUpdateStatus(entry.id, 'account', value)}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
            <SelectItem value="deleted">Deleted</SelectItem>
          </SelectContent>
        </Select>
        <Select 
          value={entry.subscription_status} 
          onValueChange={(value) => handleUpdateStatus(entry.id, 'subscription', value)}
        >
          <SelectTrigger className={`h-7 text-xs ${
            entry.subscription_status === 'subscribed' 
              ? 'border-green-300 text-green-700' 
              : 'border-orange-300 text-orange-700'
          }`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="subscribed">Subscribed</SelectItem>
            <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6" data-testid="mailing-list-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-carbon-black mb-1">Mailing List</h1>
            <p className="text-sm sm:text-base text-slate-gray">Manage your subscriber database</p>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <Button onClick={() => setShowAddModal(true)} className="gradient-fearless text-white flex-1 sm:flex-none text-sm">
              <Plus className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Add Email</span>
              <span className="sm:hidden">Add</span>
            </Button>
            <Button onClick={handleExport} variant="outline" className="flex-1 sm:flex-none text-sm">
              <Download className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">Export</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-slate-gray truncate">Total</p>
                    <p className="text-xl sm:text-2xl font-bold text-carbon-black">{stats.total}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 ml-2">
                    <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-slate-gray truncate">Subscribed</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.subscribed}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 ml-2">
                    <UserCheck className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-slate-gray truncate">Unsubscribed</p>
                    <p className="text-xl sm:text-2xl font-bold text-orange-600">{stats.unsubscribed}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 ml-2">
                    <UserX className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-slate-gray truncate">Active</p>
                    <p className="text-xl sm:text-2xl font-bold text-purple-600">{stats.active}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 ml-2">
                    <Users className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="space-y-3 sm:space-y-0 sm:flex sm:flex-wrap sm:gap-3">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-gray" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 text-sm"
                  data-testid="mailing-list-search"
                />
              </div>
              <div className="grid grid-cols-3 gap-2 sm:flex sm:gap-3">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="text-xs sm:text-sm sm:w-32">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={subscriptionFilter} onValueChange={setSubscriptionFilter}>
                  <SelectTrigger className="text-xs sm:text-sm sm:w-32">
                    <SelectValue placeholder="Subscr." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="subscribed">Subscribed</SelectItem>
                    <SelectItem value="unsubscribed">Unsub.</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="text-xs sm:text-sm sm:w-32">
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="User Registration">Register</SelectItem>
                    <SelectItem value="Contact Form">Contact</SelectItem>
                    <SelectItem value="Manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                {(searchTerm || statusFilter || subscriptionFilter || sourceFilter) && (
                  <Button variant="ghost" onClick={clearFilters} className="text-slate-gray text-sm flex-1 sm:flex-none">
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
                <Button variant="outline" onClick={() => { fetchMailingList(); fetchStats(); }} className="sm:px-3">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Entries List */}
        <Card>
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="flex items-center text-lg sm:text-xl">
              <Mail className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-fearless-blue" />
              Subscribers ({entries.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-fearless-blue" />
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="h-12 w-12 mx-auto text-slate-gray mb-4" />
                <p className="text-slate-gray text-sm sm:text-base">No entries found</p>
              </div>
            ) : (
              <>
                {/* Mobile Card Layout */}
                <div className="sm:hidden space-y-3">
                  {entries.map((entry) => (
                    <EntryCard key={entry.id} entry={entry} />
                  ))}
                </div>

                {/* Desktop Table Layout */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-gray">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-gray">Name</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-gray">Date</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-gray">Source</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-gray">Account</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-gray">Status</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-slate-gray"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => (
                        <tr key={entry.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <span className="font-medium text-carbon-black text-sm">{entry.email}</span>
                          </td>
                          <td className="py-3 px-4 text-slate-gray text-sm">
                            {entry.first_name || entry.last_name 
                              ? `${entry.first_name || ''} ${entry.last_name || ''}`.trim()
                              : '-'}
                          </td>
                          <td className="py-3 px-4 text-slate-gray text-sm">
                            {formatDate(entry.signup_date)}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              entry.source === 'User Registration' 
                                ? 'bg-blue-100 text-blue-700'
                                : entry.source === 'Contact Form'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {entry.source}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <Select 
                              value={entry.account_status} 
                              onValueChange={(value) => handleUpdateStatus(entry.id, 'account', value)}
                            >
                              <SelectTrigger className="w-24 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="disabled">Disabled</SelectItem>
                                <SelectItem value="deleted">Deleted</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-3 px-4">
                            <Select 
                              value={entry.subscription_status} 
                              onValueChange={(value) => handleUpdateStatus(entry.id, 'subscription', value)}
                            >
                              <SelectTrigger className={`w-28 h-8 text-xs ${
                                entry.subscription_status === 'subscribed' 
                                  ? 'border-green-300 text-green-700' 
                                  : 'border-orange-300 text-orange-700'
                              }`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="subscribed">Subscribed</SelectItem>
                                <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(entry.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Add Email Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg">Add Email</DialogTitle>
              <DialogDescription className="text-sm">
                Manually add a new subscriber
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddEmail} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-carbon-black mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="email@example.com"
                  required
                  className="text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-carbon-black mb-1">
                    First Name
                  </label>
                  <Input
                    value={newFirstName}
                    onChange={(e) => setNewFirstName(e.target.value)}
                    placeholder="John"
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-carbon-black mb-1">
                    Last Name
                  </label>
                  <Input
                    value={newLastName}
                    onChange={(e) => setNewLastName(e.target.value)}
                    placeholder="Doe"
                    className="text-sm"
                  />
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="w-full sm:w-auto">
                  Cancel
                </Button>
                <Button type="submit" className="gradient-fearless text-white w-full sm:w-auto" disabled={addLoading}>
                  {addLoading ? 'Adding...' : 'Add to List'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MailingListPage;
